import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  MenuItem,
  ListItemText,
  Box,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getDepartmentsByLocation, getEmployeeProduction } from "src/api/faceRegistration";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { getURL } from "src/utils/config";
import axios from "axios";
import Page from "src/components/Page";
import { DatePicker } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';

const EMPTY_CELL_VALUE = "N/A";
const OUTLINED_BORDER_COLOR = "rgba(0, 0, 0, 0.23)";
const FILTER_CONTROL_WIDTH = 220;
const INITIAL_VISIBLE_ROWS = 20;
const ROW_LOAD_BATCH_SIZE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 80;

const getFirstFieldValue = (item, fieldNames, fallback = EMPTY_CELL_VALUE) => {
  const value = fieldNames
    .map((fieldName) => item?.[fieldName])
    .find((fieldValue) => (
      fieldValue !== null &&
      fieldValue !== undefined &&
      String(fieldValue).trim() !== ""
    ));

  return value === undefined ? fallback : value;
};

const getNumberFieldValue = (item, fieldNames) => {
  const value = getFirstFieldValue(item, fieldNames, "");
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatAttendancePercentage = (item) => {
  const percentage = getFirstFieldValue(
    item,
    [
      "AttendancePercentage",
      "attendancePercentage",
      "attendance_percentage",
      "AttendancePercent",
      "attendancePercent",
      "attendance_percent",
      "Percentage",
      "percentage"
    ],
    ""
  );

  if (percentage !== "") {
    return percentage;
  }

  const totalEmployees = getNumberFieldValue(item, [
    "TotalEmployees",
    "totalEmployees",
    "total_employees",
    "TotalEmployee",
    "totalEmployee",
    "EmployeeCount",
    "employeeCount",
    "employee_count",
    "TotalCount",
    "totalCount",
    "total_count",
    "total_users"
  ]);
  const presentEmployees = getNumberFieldValue(item, [
    "Present",
    "present",
    "PresentCount",
    "presentCount",
    "present_count",
    "TotalPresent",
    "totalPresent",
    "total_present",
    "present_users"
  ]);

  if (totalEmployees && presentEmployees !== null) {
    return `${((presentEmployees / totalEmployees) * 100).toFixed(2)}%`;
  }

  return EMPTY_CELL_VALUE;
};

const normalizeDepartmentOptions = (departments) =>
  (Array.isArray(departments) ? departments : (departments ? [departments] : []))
    .map((departmentItem, index) => ({
      ...departmentItem,
      Id: departmentItem?.Id ??
        departmentItem?.ID ??
        departmentItem?.id ??
        departmentItem?.DepartmentId ??
        departmentItem?.DepartmentID ??
        departmentItem?.department_id ??
        `department-${index}`,
      DepartmentName: String(
        departmentItem?.DepartmentName ??
        departmentItem?.departmentName ??
        departmentItem?.Department ??
        departmentItem?.department ??
        departmentItem?.Name ??
        departmentItem?.name ??
        ''
      ).trim(),
    }))
    .filter((departmentItem) => departmentItem.DepartmentName);

const parseArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string" || value.trim() === "") {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    return [];
  }
};

const normalizeDailyAttendanceResponse = (data) => {
  const reportItems = Array.isArray(data) ? data : (data ? [data] : []);

  return reportItems.reduce((rows, reportItem) => {
    const attendanceItems = parseArrayValue(
      reportItem?.attendance ??
      reportItem?.Attendance ??
      reportItem?.attendance_details ??
      reportItem?.AttendanceDetails
    );

    if (attendanceItems.length === 0) {
      rows.push(reportItem);
      return rows;
    }

    attendanceItems.forEach((attendanceItem) => {
      rows.push({
        ...reportItem,
        ...attendanceItem,
      });
    });

    return rows;
  }, []);
};

const attendanceTableColumns = [
  {
    id: "sno",
    label: "S.No",
    width: 10,
    getValue: (_item, index) => index + 1
  },
  {
    id: "department",
    label: "Department",
    width: 24,
    getValue: (item) => getFirstFieldValue(item, [
      "DepartmentName",
      "departmentName",
      "Department",
      "department",
      "department_name"
    ])
  },
  {
    id: "location",
    label: "Location",
    width: 24,
    getValue: (item) => getFirstFieldValue(item, [
      "LocationName",
      "locationName",
      "Location",
      "location",
      "location_name"
    ])
  },
  {
    id: "date",
    label: "Date",
    width: 16,
    getValue: (item) => getFirstFieldValue(item, [
      "Date",
      "date",
      "AttendanceDate",
      "attendanceDate",
      "attendance_date",
      "FaceDate",
      "entry_date"
    ])
  },
  {
    id: "totalEmployees",
    label: "Total Employees",
    width: 18,
    getValue: (item) => getFirstFieldValue(item, [
      "TotalEmployees",
      "totalEmployees",
      "total_employees",
      "TotalEmployee",
      "totalEmployee",
      "EmployeeCount",
      "employeeCount",
      "employee_count",
      "TotalCount",
      "totalCount",
      "total_count",
      "total_users"
    ])
  },
  {
    id: "present",
    label: "Present",
    width: 14,
    getValue: (item) => getFirstFieldValue(item, [
      "Present",
      "present",
      "PresentCount",
      "presentCount",
      "present_count",
      "TotalPresent",
      "totalPresent",
      "total_present",
      "present_users"
    ])
  },
  {
    id: "absent",
    label: "Absent",
    width: 14,
    getValue: (item) => getFirstFieldValue(item, [
      "Absent",
      "absent",
      "AbsentCount",
      "absentCount",
      "absent_count",
      "TotalAbsent",
      "totalAbsent",
      "total_absent",
      "absent_users"
    ])
  },
  {
    id: "attendancePercentage",
    label: "Attendance Percentage",
    width: 24,
    getValue: formatAttendancePercentage
  }
];

const DailyAttendanceReport = () => {
  //const [date, setDate] = useState(dayjs());
	  const [filteredData, setFilteredData] = useState([]); // for search filter
  const [visibleRowCount, setVisibleRowCount] = useState(INITIAL_VISIBLE_ROWS);
  const [apiData, setApiData] = useState(null);
  const [locationNames, setLocationNames] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState(null);
	   const [departmentOptions, setDepartmentOptions] = useState([]);
	  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [departmentError, setDepartmentError] = useState('');
  const departmentRequestIdRef = useRef(0);


  const [loading, setLoading] = useState(false);

  const [locationError, setLocationError] = useState(false);

  const [fromDate, setFromDate] = useState(() => {

    return dayjs().startOf('day')

  });

  const [toDate, setToDate] = useState(() => {

    return dayjs().endOf('day')

  });

  const [fromDateError, setFromDateError] = useState(false);

  const [toDateError, setToDateError] = useState(false);



	  const handleFromDateChange = (date) => {
	
	    setFromDate(date ? dayjs(date).startOf('day') : null);
	
	    setFromDateError(false);

  };



	  const handleToDateChange = (date) => {
	
	    setToDate(date ? dayjs(date).endOf('day') : null);
	
	    setToDateError(false);

  };
  
  useEffect(() => {
    const fetchData = async () => {
      
      const apiUrl = getURL("employee-shifts-info/location-names?status=1");
      const AUTH_TOKEN = `${localStorage.getItem('token_type')} ${localStorage.getItem('access_token')}`;
      
      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: AUTH_TOKEN,
          },
        });

        const extractedLocationNames = response.data.map(item => item.LocationName);
        setLocationNames(extractedLocationNames);
        
        setApiData(response.data);
      } catch (error) {
        console.error('Error:', error);
      }
    };
  
    fetchData();
  }, []);



	   const updateLocation = useCallback((value) => {
	    setSelectedLocation(value);
	    const selectedLocationObject = apiData?.find((location) => location.LocationName === value);
	    const locationId = selectedLocationObject ? selectedLocationObject.Id : null;
    setSelectedLocationId(locationId);
    if (value) {
      localStorage.setItem("NetLocation", value);
    } else {
      localStorage.removeItem("NetLocation");
    }
    setLocationError(false);
    
	    // Clear previous department selections when location changes
	    setSelectedDepartments([]);
    setDepartmentOptions([]);
    setDepartmentError('');
	    
    const requestId = departmentRequestIdRef.current + 1;
    departmentRequestIdRef.current = requestId;

    if (!locationId) {
      setDepartmentLoading(false);
      return;
    }

    setDepartmentLoading(true);
    getDepartmentsByLocation({ location_id: locationId }, (response) => {
      if (departmentRequestIdRef.current !== requestId) return;

      if (response.status === 200) {
        setDepartmentOptions(normalizeDepartmentOptions(response.data));
        setDepartmentError('');
      } else {
        setDepartmentOptions([]);
        setDepartmentError('Unable to load departments');
      }
      setDepartmentLoading(false);
    });
	  }, [apiData]);

  const handleLocationChange = (value) => {
    updateLocation(value);
  };


	  const handleDepartmentChange = (_event, value) => {
    if (!selectedLocation || departmentLoading) {
      setSelectedDepartments([]);
      return;
    }
	    if (value.some((option) => option.isSelectAll)) {
	      handleSelectAllDepartments();
	      return;
	    }

	    setSelectedDepartments(value);
	  };

  // Handle Select All functionality
  const handleSelectAllDepartments = () => {
    if (!departmentOptions.length) {
      setSelectedDepartments([]);
      return;
    }
    if (selectedDepartments.length === departmentOptions.length) {
      // If all are selected, deselect all
      setSelectedDepartments([]);
    } else {
      // Select all departments
      setSelectedDepartments(departmentOptions);
    }
	  };

	  const handleSubmit = () => {
    const locationId = selectedLocationId || 0;

    setLoading(true);
	    setLocationError(false);
	    setFromDateError(false);
	    setToDateError(false);
	
    if (locationId === 0 || fromDate === null || toDate === null) {
	      if (locationId === 0) {
	        setLocationError(true);
	      }
      if (fromDate === null) {
        setFromDateError(true);
      }
	      if (toDate === null) {
	        setToDateError(true);
	      }
	      setLoading(false);
	      return;
	    }

	    const combinedFromDateTime = dayjs(fromDate).startOf('day').format('YYYY-MM-DD HH:mm:ss');
	    const combinedToDateTime = dayjs(toDate).endOf('day').format('YYYY-MM-DD HH:mm:ss');

    // Get department IDs for the selected departments
    const departmentIds = selectedDepartments.map(dept => dept.Id);

    const data = {
      location_id: locationId,
      from_date: combinedFromDateTime,
      to_date: combinedToDateTime,
      department_id: departmentIds.join(','),
     
    };
    
    console.log('Submitting with departments:', data); // For debugging
    
    getEmployeeProduction(data, (response) => {
      if (response.status === 200) {
	        setFilteredData(normalizeDailyAttendanceResponse(response.data));
	        setVisibleRowCount(INITIAL_VISIBLE_ROWS);
	      }
      setLoading(false);
    });
  };

	  const handleExportExcel = async () => {
	    // const formattedDate = dayjs(date).format("YYYY-MM-DD");
	    const combinedFromDateTime = dayjs(fromDate).format('YYYY-MM-DD');
	    const combinedToDateTime = dayjs(toDate).format('YYYY-MM-DD');
	    const formattedDate = combinedFromDateTime + '_TO_' + combinedToDateTime;
	    const exportData = filteredData;
	
	    if (exportData.length !== 0) {
	      // Create a new workbook and worksheet
	      const workbook = new ExcelJS.Workbook();
	      const worksheet = workbook.addWorksheet("Daily Attendance");
	      // Define headers with styles
	      worksheet.columns = attendanceTableColumns.map((column) => ({
	        header: column.label,
	        key: column.id,
	        width: column.width,
	        alignment: { horizontal: "center" },
	      }));
	
	      // Add rows with data
	      exportData.forEach((item, index) => {
	        const rowData = attendanceTableColumns.reduce((acc, column) => ({
	          ...acc,
	          [column.id]: column.getValue(item, index)
	        }), {});
	        const row = worksheet.addRow(rowData);
	
	        // Apply alignment styles to all cells in the row
	        row.eachCell((cell) => {
	          cell.alignment = { horizontal: "center" };
        });
      });

      // Apply styles to headers
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
      });
	
	      // Generate and save the file
	      const fileName = `DailyAttendance_${formattedDate}.xlsx`;
	      const buffer = await workbook.xlsx.writeBuffer();
	      saveAs(new Blob([buffer]), fileName);
	    } else {
      alert("No data to export.");
    }
    // });
  };

  const visibleData = filteredData.slice(0, visibleRowCount);

  const handleTableScroll = useCallback((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= SCROLL_LOAD_THRESHOLD_PX;

    if (!isNearBottom) {
      return;
    }

    setVisibleRowCount((currentCount) => (
      Math.min(currentCount + ROW_LOAD_BATCH_SIZE, filteredData.length)
    ));
  }, [filteredData.length]);

	  const errorHelperSx = {
	    minHeight: 20, // reserves space
	    mt: 0.5,
	  };

	  const filterControlSx = (hasError = false, width = FILTER_CONTROL_WIDTH) => ({
	    width,
	    minWidth: width,
	      '& .MuiOutlinedInput-root': {
	        minHeight: 56,
	        backgroundColor: 'background.paper',
	      '& fieldset': {
	        borderColor: hasError ? 'error.main' : OUTLINED_BORDER_COLOR,
	      },
	      '&:hover fieldset': {
	        borderColor: hasError ? 'error.main' : 'text.primary',
	      },
	      '&.Mui-focused fieldset': {
	        borderColor: hasError ? 'error.main' : 'primary.main',
	      },
	      '&.Mui-disabled fieldset': {
	        borderColor: hasError ? 'error.main' : OUTLINED_BORDER_COLOR,
	      },
	    },
	      '& .MuiFormHelperText-root': {
	        minHeight: 20,
	        marginLeft: 0,
	        marginRight: 0,
	      },
	      '& .MuiInputLabel-root': {
	        backgroundColor: 'background.paper',
	        px: 0.5,
	      },
	    });

  return (  
    <Page title="Production Report">
    
	        <Card>
	          <CardContent>
	            <Box
	              sx={{
	                display: 'flex',
	                alignItems: 'flex-start',
	                justifyContent: 'flex-start',
	                flexWrap: 'nowrap',
		                gap: 2,
		                mx: 2,
		                mt: 2,
		                mb: 2,
		                overflowX: 'auto',
		                pt: 1.5,
		                pb: 1,
		              }}
	            >
	
	                <FormControl sx={filterControlSx(locationError)}>
	                <Autocomplete
	                  value={selectedLocation}
	                  onChange={(_event, value) => handleLocationChange(value || "")}
	                  options={locationNames}
	                  renderInput={(params) => (
	                    <TextField
		                      {...params}
		                      label="Location"
		                      variant="outlined"
		                      error={locationError}
	                      helperText={locationError ? "Please Select Location" : " "}
	                    />
	                  )}
	                  isOptionEqualToValue={(option, value) => option === value}
	                  getOptionLabel={(option) => option || ""}
	                  noOptionsText="No locations available"
	                  disabled={locationNames.length === 0}
	                  sx={filterControlSx(locationError)}
	                />
	              </FormControl>
	
	                  <FormControl sx={filterControlSx(Boolean(departmentError))}>
	                <Autocomplete
	                  multiple
	                  limitTags={1}
	                  renderTags={() => null}
	                  value={selectedDepartments}
	                  onChange={handleDepartmentChange}
	                  options={departmentOptions}
                  loading={departmentLoading}
                  loadingText="Loading departments..."
                  noOptionsText={
                    selectedLocation
                      ? (departmentError || "No departments available")
                      : "Select location first"
                  }
                  getOptionLabel={(option) => option.DepartmentName || ""}
                  isOptionEqualToValue={(option, value) => String(option.Id) === String(value.Id)}
                  disabled={!selectedLocation || departmentLoading}
                  renderInput={(params) => (
                    <TextField
	                      {...params}
	                      label="Department"
		                      variant="outlined"
		                      error={Boolean(departmentError)}
		                      helperText={departmentError || " "}
		                      placeholder={
                          departmentLoading
                            ? "Loading departments..."
                            : !selectedLocation
                              ? "Select location first"
                              : "Select departments"
                        }
		                    />
                  )}
                 
	                  renderOption={(props, option, { selected }) => (
	                    <MenuItem {...props}>
	                      <Checkbox
	                        checked={
	                          option.isSelectAll
	                            ? departmentOptions.length > 0 && selectedDepartments.length === departmentOptions.length
	                            : selected
	                        }
	                      />
	                      <ListItemText primary={option.DepartmentName} />
	                    </MenuItem>
	                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  componentsProps={{
                    popper: {
                      placement: 'bottom-start',
                    },
                  }}
		                  sx={filterControlSx(Boolean(departmentError))}
                  // Add Select All option at the top of the list
	                  filterOptions={(options) => {
                    if (!selectedLocation || departmentLoading || options.length === 0) {
                      return options;
                    }
                    // Add "Select All" as first option
                    const selectAllOption = {
                      Id: -1,
                      DepartmentName: 'Select All',
                      isSelectAll: true,
                    };
                    return [selectAllOption, ...options];
                  }}
                />
              </FormControl>
	
	
	
	
	                <LocalizationProvider dateAdapter={AdapterDayjs}>
	                  <FormControl sx={filterControlSx(fromDateError)}>
		                    <DatePicker
		                      label="From Date"
		                      id="from-date"
	                      format="YYYY-MM-DD"
		                      value={fromDate}
		                      onChange={handleFromDateChange}
		                      maxDate={dayjs()}
	                      slotProps={{
		                        textField: {
		                          fullWidth: true,
		                          error: fromDateError,
		                        },
	                      }}
	                    />

	                    <Typography color="error" variant="body2" sx={errorHelperSx}>
	                      {fromDateError ? "Please select From Date" : " "}
	                    </Typography>
                  </FormControl>
                </LocalizationProvider>
	
	
	                <LocalizationProvider dateAdapter={AdapterDayjs}>
	                  <FormControl sx={filterControlSx(toDateError)}>
		                    <DatePicker
		                      label="To Date"
		                      id="to-date"
	                      format="YYYY-MM-DD"
	                      value={toDate}
		                      onChange={handleToDateChange}
	                      minDate={fromDate}
	                      maxDate={dayjs()}
	                      slotProps={{
		                        textField: {
		                          fullWidth: true,
		                          error: toDateError,
		                        },
	                      }}
	                    />
                    <Typography color="error" variant="body2" sx={errorHelperSx}>
                      {toDateError ? "Please Select To Date" : " "}
                    </Typography>
                  </FormControl>

                </LocalizationProvider>

             
	                  <Box sx={{ display: 'flex', gap: 1, minHeight: 56, alignItems: 'center', pt: 0 }}>
	                    <LoadingButton type="submit" variant="contained" onClick={handleSubmit} loading={loading}>
	                      Submit
	                    </LoadingButton>

                    <LoadingButton type="submit" variant="contained" onClick={handleExportExcel}>
                      Export
                    </LoadingButton>
                  </Box>

	                 
	              </Box>

	            <Box sx={{ width: '100%', overflowX: 'auto' }}>
	              <TableContainer
                  onScroll={handleTableScroll}
                  sx={{ minWidth: 1100, maxHeight: '65vh', overflow: 'auto' }}
                >
	                <Table stickyHeader sx={{ minWidth: 1100 }}>
	                  <TableHead>
	                    <TableRow>
	                      {attendanceTableColumns.map((column) => (
	                        <TableCell key={column.id}>{column.label}</TableCell>
	                      ))}
	                    </TableRow>
	                  </TableHead>
	                  <TableBody>
	                    {visibleData.map((item, index) => {
	                      const absoluteIndex = index;

	                      return (
	                        <TableRow key={absoluteIndex}>
	                          {attendanceTableColumns.map((column) => (
	                            <TableCell key={column.id}>
	                              {column.getValue(item, absoluteIndex)}
	                            </TableCell>
	                          ))}
	                        </TableRow>
	                      );
	                    })}
	                  </TableBody>
	                </Table>
	              </TableContainer>
                {filteredData.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
                    Showing {visibleData.length} of {filteredData.length}
                  </Typography>
                )}
            </Box>
          </CardContent>
        </Card>

	    </Page>
  );
};

export default DailyAttendanceReport;
